import { Component, EventEmitter, Input, Output, TemplateRef, ViewChild } from '@angular/core';
import moment from 'moment-mini';
import { catchError, filter, switchMap } from 'rxjs/operators';
import { NgbCustomModalComponent } from 'components/modal-windows/ngb-custom-modal/ngb-custom-modal.component';
import {
  SETTINGS_MODAL_CONFIG
} from 'features/organization-settings/notifications/organization-notifications.constants';
import { defer, EMPTY, Observable, of } from 'rxjs';
import { RegistryDetailsService } from 'features/registry-details/registry-details.service';
import { NgbModal, NgbModalRef } from '@ng-bootstrap/ng-bootstrap';
import { SmartModalService } from '@certemy/ui-components';
import { IPsvButton, IRegistryDetails } from 'features/on-demand-psv/on-demand-psv.interface';

const CONFIRM_MESSAGE =
  'We will initiate a PSV request for the credential in question. Please note that the time it takes to return as result is based on the entity that administers the credential in question. You will receive a notification once the result has been retrieved, according to the rules set up in your Notifications settings. You may also receive an email indicating that additional information is required to complete the PSV request. Please confirm you would like to proceed.';
const ERROR_TEMPLATE_MESSAGE =
  'We cannot initiate a PSV request at this time due to the error(s) below. Please correct before proceeding.';

@Component({
  selector: 'cm-on-demand-psv-button',
  templateUrl: './on-demand-psv-button.component.html',
  styleUrls: ['./on-demand-psv-button.component.scss'],
})
export class OnDemandPsvButtonComponent {
  @ViewChild('errorTemplate') errorTemplate: TemplateRef<any>;
  @Input() buttonField: IPsvButton;
  @Input() registry: IRegistryDetails;
  @Output() initPsv = new EventEmitter<boolean>();
  moreOptions = [{ id: 0, title: 'Submit New PSV Request' }];
  errorsArray = [];
  ERROR_TEMPLATE_MESSAGE = ERROR_TEMPLATE_MESSAGE;
  private previewModalRef: NgbModalRef;

  constructor(
    private registryDetailsService: RegistryDetailsService,
    private smartModalService: SmartModalService,
    private modalService: NgbModal,
  ) {}

  initiatePsv(event, field): void {
    const { profileId, acronym, lastVerifiedDate } = field;
    const date = lastVerifiedDate ? moment(new Date(lastVerifiedDate)).format('LL') : lastVerifiedDate;
    if (event) {
      event.stopPropagation();
      event.currentTarget.blur();
    }
    this.validatePsvRequest({ profileId, acronym, lastVerifiedDate: date });
  }

  private validatePsvRequest(event: { profileId: number; acronym: string; lastVerifiedDate: Date }): void {
    this.registryDetailsService
      .validatePsvRequest(event.profileId, event.acronym)
      .pipe(
        catchError((error) => this.showErrorModal(error)),
        switchMap(() =>
          defer(() =>
            !!event.lastVerifiedDate
              ? this.smartModalService.openConfirm(
                  this.createModalConfig('', this.showConfirmMessage(event.lastVerifiedDate)),
                )
              : of(true),
          ),
        ),
        filter((data) => !!data),
        switchMap(() => {
          return this.smartModalService.openConfirm(this.createModalConfig('PLEASE READ', CONFIRM_MESSAGE));
        }),
        filter((data) => !!data),
        switchMap(() =>
          this.registryDetailsService.processPsvRequest(event.profileId, event.acronym).pipe(catchError(() => EMPTY)),
        ),
      )
      .subscribe(() => {
        this.initPsv.emit(true);
      });
  }

  private createModalConfig(title: string, body: string): { title: string; body: string; successButtonText: string } {
    return { title, body, successButtonText: 'Continue' };
  }

  private showConfirmMessage(date: Date): string {
    return `This license was last verified on ${date}. You may view the history of that request by clicking the “History” link at right.
                  Are you sure you wish to initiate another PSV request?`;
  }

  private showErrorModal(error: any): Observable<never> {
    this.errorsArray = error.body.error_details.errors;
    this.previewModalRef = this.modalService.open(NgbCustomModalComponent, {
      backdrop: 'static',
      keyboard: false,
      windowClass: 'x-m',
    });
    this.previewModalRef.componentInstance.template = this.errorTemplate;
    this.previewModalRef.componentInstance.config = {
      ...SETTINGS_MODAL_CONFIG,
      title: 'WARNING',
      hideHeader: false,
    };

    return EMPTY;
  }

  closeChildModal(): void {
    this.previewModalRef.close();
  }
}
